import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {FallingObjectsSpawner} from './falling-objects-spawner.js';
import {LevelSelect} from './level-select.js';
import {BoardColor} from './board-object.js';
import {Puzzle} from './puzzle.js';
import {RNG} from '../utils/rng.js';
import {FruitPrefabs} from './fruit-prefabs.js';

export interface SpecialSpawnRules {
    objectToSpawn: string;
    minTurnsBeforeSpawn: number;
    minTurnsAfterOtherSpecial: number;
    minLevelBeforeSpawn: number;
    spawnLikelihood: number;
    lastSpawnTurn: number;
}

/**
 * Spawns falling objects like fruits
 *
 * Notes:
 * - The original had an arraw of levelDependentObjects.
 *   But that was never used so it's not ported. It is possible to add
 *   it again later if we want to add that feature in.
 */
export class DefaultSpawner extends FallingObjectsSpawner {
    static TypeName = 'default-spawner';
    static InheritProperties = true;

    @property.int(3)
    spawnRepeatLimit = 3;

    // since it's not possible to have a list of objects in the editor the
    // list is hardcoded for now. These are the names that are defined in the Fruits.wlp file
    // prettier-ignore
    public objectsToSpawn: Map<BoardColor, string> = new Map([
        [BoardColor.Blue, 'Fruit Blue'],
        [BoardColor.Orange, 'Fruit Orange'],
        [BoardColor.Green,'Fruit Green'],
        [BoardColor.Red,'Fruit Red'],
        [BoardColor.Yellow,'Fruit Yellow'],
        [BoardColor.Pink,'Fruit Pink'],
        [BoardColor.Purple,'Fruit Purple'],
        [BoardColor.Moon,'Fruit Moon'],
        [BoardColor.Brown,'Fruit Brown'],
        [BoardColor.Turquoise,'Fruit Turquoise']]);

    public specialObjects: SpecialSpawnRules[] = [
        {
            objectToSpawn: 'Fruit Rainbow',
            spawnLikelihood: 0,
            minTurnsBeforeSpawn: 0,
            minTurnsAfterOtherSpecial: 0,
            minLevelBeforeSpawn: 0,
            lastSpawnTurn: 0,
        },
    ];

    private _turnCount: number = 0;
    private _lastSpawnIndex: number = -1;
    private _lastSpawnCount: number = 0;
    private _lastSpecialTurn: number = 0;

    override initialize() {
        const levelSelect = LevelSelect.instance;
        const keysToRemove: BoardColor[] = [];
        for (const [color] of this.objectsToSpawn) {
            if (!levelSelect.colorAcceptable(color)) {
                keysToRemove.push(color);
            }
        }
        for (const color of keysToRemove) {
            this.objectsToSpawn.delete(color);
        }
    }

    protected override spawnNext() {
        let obj = this._trySpawnSpecial();
        if (!obj) {
            obj = this._spawnObject();
        } else {
            this._lastSpawnIndex = -1;
            this._lastSpawnCount = 0;
        }

        this._turnCount++;

        return obj;
    }

    private _spawnObject() {
        const startIndex = RNG.getUniformInt(0, this.objectsToSpawn.size - 1);

        let spawnIndex = 0;

        for (let x = 0; x < this.objectsToSpawn.size; x++) {
            const index = (startIndex + x) % this.objectsToSpawn.size;
            if (
                index == this._lastSpawnIndex &&
                this._lastSpawnCount >= this.spawnRepeatLimit
            ) {
                continue;
            }
            spawnIndex = index;
            break;
        }

        if (this._lastSpawnIndex != spawnIndex) {
            this._lastSpawnIndex = spawnIndex;
            this._lastSpawnCount = 1;
        } else {
            this._lastSpawnCount++;
        }
        const obj2Spawn = Array.from(this.objectsToSpawn.entries())[startIndex];

        return FruitPrefabs.instance.spawn(obj2Spawn[1]);
    }

    private _trySpawnSpecial() {
        let spawn: Object3D = null;
        // // TODO: begin searching array at random index to avoid sequencing?
        for (let x = 0; x < this.specialObjects.length; x++) {
            if (
                this.specialObjects[x].minTurnsBeforeSpawn <=
                    this._turnCount - this.specialObjects[x].lastSpawnTurn &&
                this.specialObjects[x].minTurnsAfterOtherSpecial <=
                    this._turnCount - this._lastSpecialTurn &&
                this.specialObjects[x].minLevelBeforeSpawn <= Puzzle.instance.level
            ) {
                if (Math.random() <= this.specialObjects[x].spawnLikelihood) {
                    spawn = FruitPrefabs.instance.spawn(
                        this.specialObjects[x].objectToSpawn
                    );

                    this.specialObjects[x].lastSpawnTurn = this._turnCount;
                    this._lastSpecialTurn = this._turnCount;
                    break;
                }
            }
        }
        return spawn;
    }
}
